/**
 * Phase J.3.3 — "Billing schedule" card for the rental detail page.
 * Lazily loads /admin/rentals/{id}/billing-schedule. Renders one of:
 *   - empty state with "Set up recurring billing" CTA
 *   - active/paused schedule card with pause/resume/end/edit actions
 */
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Repeat, Pause, Play, CircleSlash, Pencil, Loader2, Lock, Info, AlertCircle, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import BillingScheduleModal from "@/components/BillingScheduleModal";
import { ScheduleCatchupModal } from "@/components/ResumeScheduleModal";

function StatusPill({ status }) {
    const m = {
        active: ["Active", "bg-emerald-50 text-emerald-900 ring-emerald-200"],
        paused: ["Paused", "bg-amber-50 text-amber-900 ring-amber-200"],
        ended:  ["Ended",  "bg-slate-100 text-slate-700 ring-slate-200"],
    };
    const [label, cls] = m[status] || [status || "—", "bg-slate-100 text-slate-700 ring-slate-200"];
    return (
        <span className={"inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset " + cls}>
            {label}
        </span>
    );
}


function HowBillingWorksCard() {
    // Phase RENTAL-COLLAPSE — default COLLAPSED (reference-only help copy).
    const KEY = "admin.rentalDetail.howBillingWorksCollapsed";
    const [collapsed, setCollapsed] = useState(() => {
        try { return (localStorage.getItem(KEY) ?? "1") === "1"; }
        catch { return true; }
    });
    const toggle = () => {
        setCollapsed((prev) => {
            const next = !prev;
            try { localStorage.setItem(KEY, next ? "1" : "0"); } catch { /* noop */ }
            return next;
        });
    };
    const onKey = (e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); }
    };
    return (
        <section
            data-testid="b1-how-billing-works"
            className="rounded-lg border border-blue-200 bg-blue-50/60 p-4"
        >
            <div
                role="button" tabIndex={0}
                aria-expanded={!collapsed}
                onClick={toggle} onKeyDown={onKey}
                className="flex items-start gap-3 cursor-pointer select-none"
                data-testid="b1-how-billing-works-toggle"
            >
                <span
                    className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700 ring-1 ring-inset ring-blue-200"
                    aria-hidden="true"
                >
                    {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                </span>
                <p className="text-sm font-semibold text-blue-950 mt-1">How billing works</p>
            </div>
            <div className={`overflow-hidden transition-all duration-200 ${collapsed ? "max-h-0 opacity-0" : "max-h-96 opacity-100 mt-3"}`}>
                <div className="text-sm text-blue-950 pl-11">
                    <p className="text-blue-900/85 leading-relaxed">
                        Rentals are not auto-billed when activated. To start invoicing, set up a billing schedule below.
                    </p>
                    <ul className="mt-2 space-y-1 text-xs text-blue-900/85 list-disc pl-4">
                        <li>Schedules generate invoices on the configured day of the month.</li>
                        <li>Invoices are auto-sent to the customer&apos;s billing email.</li>
                        <li>If <span className="font-mono">auto_charge</span> is on AND a payment method is on file, the saved bank account is debited automatically.</li>
                        <li>Optional: prorate the first invoice if the rental started mid-period.</li>
                    </ul>
                </div>
            </div>
        </section>
    );
}


export function BillingScheduleCard({ rentalId, role = "super_admin" }) {
    // Phase B1 follow-up #4 — admin can now manage per-rental billing
    // schedules (create / edit / pause / resume / end). Bulk operations
    // remain super_admin-only and live on a separate page.
    const canManage = role === "super_admin" || role === "admin";
    const [schedule, setSchedule] = useState(null);
    const [loading, setLoading] = useState(true);
    const [acting, setActing] = useState(null);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    // Phase RECURR-RESUME-CATCHUP (2026-07-28) — resume modal prompts
    // admins to backfill draft invoices missed during the pause window
    // before the schedule flips back to active.
    const [showResumeModal, setShowResumeModal] = useState(false);
    // Phase RECURR-RESUME-CATCHUP-EXT (2026-07-28) — after saving edits
    // to an existing schedule we check for historical/gap occurrences
    // and surface a subtle banner + explicit "Review & backfill" click
    // (banner-first UX — never auto-opens the catchup modal).
    const [editCatchupBanner, setEditCatchupBanner] = useState(null); // {count} | null
    const [showEditCatchupModal, setShowEditCatchupModal] = useState(false);
    // Phase J.3.4 — when the schedule's PM id is known, resolve the
    // customer's saved methods so the pill can show "Wells Fargo ••••6789"
    // instead of the PM id's last 4 characters.
    const [pmInfo, setPmInfo] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/rentals/${rentalId}/billing-schedule`);
            setSchedule(data);
            setError(null);
        } catch (e) {
            if (e?.response?.status === 404) {
                setSchedule(null); setError(null);
            } else {
                setError(formatApiError(e));
            }
        } finally {
            setLoading(false);
        }
    }, [rentalId]);

    useEffect(() => { load(); }, [load]);

    // Phase B1 follow-up #3 — Auto-open the edit modal when the URL has
    // ``?openSchedule=<schedule_id>`` and it matches our loaded schedule.
    // Used by the post-release/import toast and by the bulk-backfill
    // "Edit" deep-link. Strips the param once consumed so refresh doesn't
    // re-open the modal.
    const location = useLocation();
    const navigate = useNavigate();
    useEffect(() => {
        if (!schedule?.id) return;
        const params = new URLSearchParams(location.search);
        const want = params.get("openSchedule");
        if (!want) return;
        if (want === schedule.id || want === "auto") {
            setShowModal(true);
            params.delete("openSchedule");
            const next = params.toString();
            navigate(
                `${location.pathname}${next ? `?${next}` : ""}${location.hash || ""}`,
                { replace: true },
            );
        }
    }, [schedule?.id, location.pathname, location.search, location.hash, navigate]);

    // Resolve bank name + last4 for the schedule's payment method.
    useEffect(() => {
        let cancelled = false;
        async function resolvePm() {
            setPmInfo(null);
            if (!schedule?.auto_charge || !schedule?.payment_method_id) return;
            try {
                const { data: r } = await api.get(`/admin/rentals/${rentalId}`);
                const orgId = r?.customer_id || r?.customer_org_id;
                if (!orgId) return;
                const { data: pms } = await api.get(`/admin/customers/${orgId}/payment-methods`);
                if (cancelled) return;
                const match = (pms?.items || []).find(
                    (p) => p.id === schedule.payment_method_id,
                );
                if (match) setPmInfo({ bank_name: match.bank_name, last4: match.last4 });
            } catch {
                /* ignore — pill falls back to PM-id last 4 */
            }
        }
        resolvePm();
        return () => { cancelled = true; };
    }, [schedule, rentalId]);

    const handleAction = async (path, successMsg) => {
        if (!schedule) return;
        setActing(path);
        try {
            const { data } = await api.post(`/admin/billing-schedules/${schedule.id}/${path}`);
            setSchedule(data);
            toast.success(successMsg);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setActing(null);
        }
    };

    if (loading) {
        return (
            <section data-testid="billing-schedule-card" className="bg-white rounded-lg ring-1 ring-slate-200 p-4">
                <p className="text-sm text-slate-500 inline-flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                    Loading billing schedule…
                </p>
            </section>
        );
    }

    if (!schedule) {
        return (
            <div className="space-y-3" data-testid="billing-schedule-card-wrapper">
                <HowBillingWorksCard />
                <section data-testid="billing-schedule-card" className="bg-white rounded-lg ring-1 ring-slate-200 p-5">
                    <header className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
                                <Repeat size={16} aria-hidden="true" />
                            </span>
                            <div>
                                <h3 className="text-sm font-semibold text-slate-900">Recurring billing</h3>
                                <p className="text-xs text-slate-500 mt-0.5">No recurring billing for this rental.</p>
                            </div>
                        </div>
                        {canManage && (
                            <Button
                                type="button"
                                size="sm"
                                onClick={() => setShowModal(true)}
                                data-testid="billing-schedule-setup-cta"
                                className="gap-1.5"
                            >
                                <Repeat size={14} aria-hidden="true" />
                                Set up recurring billing
                            </Button>
                        )}
                    </header>
                    {error && <p className="text-xs text-rose-700 mt-2">{error}</p>}
                    {showModal && (
                        <BillingScheduleModal
                            open={showModal}
                            onClose={() => setShowModal(false)}
                            rentalId={rentalId}
                            onSaved={(s) => { setSchedule(s); setShowModal(false); }}
                        />
                    )}
                </section>
            </div>
        );
    }

    return (
        <section data-testid="billing-schedule-card" className="bg-white rounded-lg ring-1 ring-slate-200 p-5">
            <header className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
                        <Repeat size={16} aria-hidden="true" />
                    </span>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-sm font-semibold text-slate-900">Recurring billing</h3>
                            <StatusPill status={schedule.status} />
                            {schedule.auto_charge && (
                                <span
                                    data-testid="billing-schedule-auto-charge-pill"
                                    className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold bg-purple-50 text-purple-800 ring-1 ring-inset ring-purple-200"
                                >
                                    Auto-charge
                                    {pmInfo ? (
                                        <span className="ml-1 text-purple-700/80">
                                            {pmInfo.bank_name} <span className="font-mono">••••{pmInfo.last4}</span>
                                        </span>
                                    ) : schedule.payment_method_id ? (
                                        <span className="ml-1 font-mono text-purple-700/80">
                                            ••••{(schedule.payment_method_id || "").slice(-4)}
                                        </span>
                                    ) : null}
                                </span>
                            )}
                            {schedule.created_via && schedule.created_via !== "manual" && (
                                <span
                                    data-testid="b1-auto-created-badge"
                                    className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-amber-50 text-amber-900 ring-1 ring-inset ring-amber-200"
                                    title={`Auto-created when rental was ${schedule.created_via === "release_auto" ? "released" : "imported"}`}
                                >
                                    <AlertCircle size={10} aria-hidden="true" />
                                    Auto-created on {schedule.created_via === "release_auto" ? "release" : "import"} · review and confirm
                                </span>
                            )}
                            {schedule.prorate_first_period && schedule.first_period_pending && (
                                <span
                                    data-testid="b1-prorate-pending-badge"
                                    className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold bg-blue-50 text-blue-900 ring-1 ring-inset ring-blue-200"
                                >
                                    Prorate first invoice
                                </span>
                            )}
                        </div>
                        <p className="text-sm text-slate-700 mt-1">{schedule.cadence_summary}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                            ${(schedule.amount_cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })} per cycle · Net {schedule.payment_terms_days}
                        </p>
                    </div>
                </div>
                {canManage && schedule.status !== "ended" && (
                    <div className="flex items-center gap-1.5 flex-wrap justify-end">
                        <Button
                            type="button" size="sm" variant="outline"
                            onClick={() => setShowModal(true)}
                            data-testid="billing-schedule-edit"
                            className="gap-1.5"
                        >
                            <Pencil size={12} aria-hidden="true" />
                            Edit
                        </Button>
                        {schedule.status === "active" ? (
                            <Button
                                type="button" size="sm" variant="outline"
                                onClick={() => handleAction("pause", "Schedule paused")}
                                disabled={acting === "pause"}
                                data-testid="billing-schedule-pause"
                                className="gap-1.5"
                            >
                                <Pause size={12} aria-hidden="true" />
                                Pause
                            </Button>
                        ) : (
                            <Button
                                type="button" size="sm" variant="outline"
                                onClick={() => setShowResumeModal(true)}
                                disabled={acting === "resume"}
                                data-testid="billing-schedule-resume"
                                className="gap-1.5"
                            >
                                <Play size={12} aria-hidden="true" />
                                Resume
                            </Button>
                        )}
                        <Button
                            type="button" size="sm" variant="outline"
                            onClick={() => {
                                if (window.confirm("End this recurring billing? This is permanent.")) {
                                    handleAction("end", "Schedule ended");
                                }
                            }}
                            disabled={acting === "end"}
                            data-testid="billing-schedule-end"
                            className="gap-1.5 text-rose-700 hover:text-rose-900 border-rose-200 hover:bg-rose-50"
                        >
                            <CircleSlash size={12} aria-hidden="true" />
                            End
                        </Button>
                    </div>
                )}
            </header>

            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3 mt-4 text-sm">
                <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Next run</dt>
                    <dd className="text-slate-900 mt-0.5 font-mono">{schedule.next_run_date || "—"}</dd>
                </div>
                <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Last run</dt>
                    <dd className="text-slate-900 mt-0.5 font-mono">{schedule.last_run_date || "—"}</dd>
                </div>
                <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Starts</dt>
                    <dd className="text-slate-900 mt-0.5 font-mono">{schedule.starts_at}</dd>
                </div>
                <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Ends</dt>
                    <dd className="text-slate-900 mt-0.5 font-mono">{schedule.end_date || "Never"}</dd>
                </div>
            </dl>
            {schedule.notes && (
                <div className="mt-4 text-xs text-slate-600 bg-slate-50 rounded px-3 py-2 ring-1 ring-slate-100 inline-flex items-start gap-1.5">
                    <Lock size={11} className="mt-0.5 text-slate-400" aria-hidden="true" />
                    <span>{schedule.notes}</span>
                </div>
            )}

            {showModal && (
                <BillingScheduleModal
                    open={showModal}
                    onClose={() => setShowModal(false)}
                    rentalId={rentalId}
                    schedule={schedule}
                    onSaved={async (s) => {
                        setSchedule(s);
                        setShowModal(false);
                        // Phase RECURR-RESUME-CATCHUP-EXT — after saving
                        // edits to an existing schedule, silently check
                        // for missed occurrences and surface a banner
                        // (never auto-open the modal). Fire-and-forget:
                        // banner appears only when count > 0.
                        try {
                            const { data } = await api.get(
                                `/admin/billing-schedules/${s.id}/missed-occurrences?context=edit`,
                            );
                            const cnt = (data.missed_occurrences || []).length;
                            if (cnt > 0) setEditCatchupBanner({ count: cnt });
                            else setEditCatchupBanner(null);
                        } catch {
                            // Non-fatal — banner just doesn't appear.
                        }
                    }}
                />
            )}

            {editCatchupBanner && (
                <div
                    data-testid="edit-catchup-banner"
                    className="mt-4 flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                >
                    <span className="inline-flex items-center gap-2">
                        <AlertCircle size={14} aria-hidden="true" className="text-amber-700" />
                        Edit saved. <span className="font-medium">{editCatchupBanner.count} missed occurrence{editCatchupBanner.count === 1 ? "" : "s"}</span> detected.
                    </span>
                    <div className="flex items-center gap-1.5">
                        <Button
                            type="button" size="sm" variant="ghost"
                            onClick={() => setEditCatchupBanner(null)}
                            data-testid="edit-catchup-banner-dismiss"
                            className="h-7 text-amber-900 hover:bg-amber-100"
                        >
                            Dismiss
                        </Button>
                        <Button
                            type="button" size="sm"
                            onClick={() => setShowEditCatchupModal(true)}
                            data-testid="edit-catchup-banner-review"
                            className="h-7 gap-1.5 bg-amber-600 hover:bg-amber-700 text-white"
                        >
                            Review &amp; backfill
                        </Button>
                    </div>
                </div>
            )}

            {showResumeModal && (
                <ScheduleCatchupModal
                    open={showResumeModal}
                    onOpenChange={setShowResumeModal}
                    schedule={schedule}
                    mode="resume"
                    onResumed={(data) => {
                        // ``data`` shape: { ...public_schedule, backfilled_invoices: [...] }
                        const { backfilled_invoices: _bf, ...next } = data || {};
                        if (next && next.id) setSchedule(next);
                        else load();
                    }}
                />
            )}

            {showEditCatchupModal && (
                <ScheduleCatchupModal
                    open={showEditCatchupModal}
                    onOpenChange={setShowEditCatchupModal}
                    schedule={schedule}
                    mode="edit"
                    onBackfilled={() => {
                        // Clear the banner once drafts were issued.
                        setEditCatchupBanner(null);
                    }}
                />
            )}
        </section>
    );
}

export default BillingScheduleCard;
